import * as React from 'react';
import { zineColors, zineExtraColors, zineFonts, zinePairColors } from '../p2e/zine/zine-theme';

/**
 * Root class that scopes all Programme-calendar styles. Deliberately NOT the
 * zine page's `.z-issue` so the two stylesheets can never collide — every
 * rule below is byte-identical to the design spec, only the scope selector
 * differs.
 */
export const PROGRAMME_ROOT_CLASS = 'z-cal';

/**
 * Per-kind ink pair (V7 "research baseline" contrast fix): the bright spot
 * ink colors non-text marks (mini-month dots, legend dots), while small label
 * text (the `.z-ek` kind stamp, the modal kind line) uses an AA-safe variant
 * with ≥ 4.5:1 contrast on the paper background. Where the spot ink already
 * passes AA, both members are the same hex.
 */
export type ProgrammeInkPair = {
  /** Bright spot ink for dots (can be lower-contrast — it is not text). */
  dot: string;
  /** AA-safe ink (≥ 4.5:1 on #FFF8F5) for small label text. */
  label: string;
};

/** CLASS kind (group + private classes): poster red — passes AA (5.05:1). */
export const programmeClassInks: ProgrammeInkPair = {
  dot: zinePairColors[0].solid, // #D41920
  label: zinePairColors[0].solid, // #D41920 (5.05:1 on paper)
};

/** TUTOR kind: print gold dot, deep-ochre label (spec's --gold-aa). */
export const programmeTutorInks: ProgrammeInkPair = {
  dot: zinePairColors[1].solid, // #C79100 (2.67:1 — dot only)
  label: '#8A6400', // 5.12:1 on paper
};

/**
 * Deterministic ink-pair palette for community-event categories, starting
 * with the design's plum and continuing with the zine's other spot inks. The
 * `zineExtraColors` tones were audited for this role but are mostly tints and
 * shadow colors that fail at 9px-dot / small-caps size on the paper
 * background (or collide with the class red), so the palette leans on
 * `zinePairColors` and keeps only the two usable extras as overflow.
 * Label partners are darkened same-hue values wherever the dot ink fails
 * AA (4.5:1) on the paper background.
 */
export const programmeCommunityInkPairs: readonly ProgrammeInkPair[] = [
  { dot: zinePairColors[3].solid, label: '#6B2F96' }, // #7C3AAB plum (6.56:1) → spec's --plum-aa (8.09:1)
  { dot: zinePairColors[4].solid, label: zinePairColors[4].solid }, // #0E7490 teal (5.10:1 — passes)
  { dot: zinePairColors[6].solid, label: '#AB3478' }, // #D4589A magenta (3.53:1) → dark magenta (5.72:1)
  { dot: zinePairColors[7].solid, label: zinePairColors[7].solid }, // #2B5FB8 blue (5.83:1 — passes)
  { dot: zinePairColors[5].solid, label: '#A85312' }, // #D96A1B orange (3.32:1) → dark orange (5.11:1)
  { dot: zinePairColors[2].solid, label: zinePairColors[2].solid }, // #2E7D5B green (4.76:1 — passes)
  { dot: zineExtraColors.gradientMid, label: '#BA2E49' }, // #F05F7A pink (3.02:1) → dark rose (5.59:1)
  { dot: zineExtraColors.posterDark, label: zineExtraColors.posterDark }, // #2A1B23 (15.63:1 — passes)
];

/**
 * The full "Calendar D — The Programme" stylesheet, transposed 1:1 from the
 * design spec. Every rule is scoped under `.z-cal` (a pure scope-selector
 * swap from the spec's `.z-issue`); values, shadows, rotations, masks,
 * clamp()s and media queries are verbatim. Token hexes are interpolated from
 * `zine-theme.ts` where they match, so the palette stays single-sourced.
 *
 * One deliberate generalization from the spec: the spec's fixed
 * `.k-group/.k-private/.k-community` color hooks are replaced by per-element
 * custom properties (`--k-ink` on entries/modal kind line, `--k-dot`/
 * `--k-dot2` on mini-month cells) so the kind system is data-driven. Dot
 * size/position, the multi-dot shadow trick and every other declaration are
 * unchanged.
 *
 * Event-row typography follows the later "V7 · Research baseline" specimen
 * (which supersedes the artifact for exactly these selectors): `.z-day`
 * padding, `.z-entry .z-et/.z-time/.z-ek` and the new `.z-entry .z-sub`
 * sub-line. Everything else keeps the artifact rules.
 */
export const programmeCalendarCss = `
.z-cal {
  --z-paper: ${zineColors.paper};
  --z-cream: ${zineColors.cream};
  --z-ink: ${zineColors.ink};
  --z-mid: ${zineColors.mid};
  --z-warm-gray: ${zineColors.warmGray};
  --z-red: ${zineColors.red};
  --z-red-applied: ${zineColors.redApplied};
  --z-sizzle: ${zineColors.sizzle};
  --z-charm: ${zineColors.charm};
  --z-baby: ${zineColors.baby};
  --z-gold: ${zineColors.gold};
  --z-line: ${zineColors.line};
  --z-good: ${zineColors.good};

  --z-font-cond: ${zineFonts.cond};
  --z-font-serif: ${zineFonts.serif};
  --z-font-ui: ${zineFonts.ui};

  color-scheme: light;
  margin: 0; background: var(--z-paper); color: var(--z-ink);
  font-family: var(--z-font-ui); line-height: 1.6; font-size: 1rem;
}
.z-cal, .z-cal *, .z-cal *::before, .z-cal *::after { box-sizing: border-box; }
html:has(.z-cal) { scroll-behavior: smooth; }
@media (prefers-reduced-motion: reduce) {
  html:has(.z-cal) { scroll-behavior: auto; }
  .z-cal, .z-cal *, .z-cal *::before, .z-cal *::after { animation: none !important; transition: none !important; }
}
.z-cal button { font-family: inherit; cursor: pointer; }
.z-cal button:focus-visible, .z-cal a:focus-visible { outline: 2px solid var(--z-red); outline-offset: 2px; }
.z-cal .z-page { max-width: 880px; margin: 0 auto; padding: 0 1.4rem; }

/* ═══ MONTH CONTROL BAR ═══ */
.z-cal .z-monthbar { display: flex; align-items: center; gap: .6rem; flex-wrap: wrap; align-self: center; justify-self: end; }
.z-cal .z-nav-btn {
  font-family: var(--z-font-cond); font-size: .72rem; font-weight: 700; letter-spacing: .18em; text-transform: uppercase;
  border: 2px solid var(--z-ink); border-radius: 999px; padding: .42rem 1rem;
  background: none; color: var(--z-ink); transition: background .13s, color .13s;
}
.z-cal .z-nav-btn:hover { background: var(--z-ink); color: var(--z-paper); }
.z-cal .z-nav-btn--today { background: var(--z-red); border-color: var(--z-red); color: #fff; }
.z-cal .z-nav-btn--today:hover { background: #B5131A; border-color: #B5131A; color: #fff; }
.z-cal .z-mlabel {
  font-family: var(--z-font-cond); font-weight: 700; font-size: 1rem;
  letter-spacing: .14em; text-transform: uppercase; padding: 0 .3rem;
  font-variant-numeric: tabular-nums;
}
.z-cal .z-join-btn {
  border: 0; border-radius: 999px; padding: .55rem 1.3rem;
  font-family: var(--z-font-cond); font-weight: 700; font-size: .78rem; letter-spacing: .18em; text-transform: uppercase;
  background: linear-gradient(90deg, #CF2E2E, #F05F7A 55%, #F78DA7); color: #fff;
  box-shadow: 0 4px 0 #8E1218, 0 12px 22px -10px rgba(212,25,32,.55);
  transition: transform .13s, box-shadow .13s;
}
.z-cal .z-join-btn:hover { transform: translateY(-2px); }
.z-cal .z-join-btn:active { transform: translateY(3px); box-shadow: 0 1px 0 #8E1218; }

/* ═══ MONTH SPREAD ═══ */
.z-cal .z-spread .z-page { padding-top: 2.6rem; padding-bottom: 3rem; }
.z-cal .z-spread-head { display: grid; grid-template-columns: auto 1fr auto; gap: 1.4rem; align-items: start; margin-bottom: 1.8rem; }
.z-cal .z-bigno {
  font-family: var(--z-font-cond); font-weight: 700; font-size: 4.6rem; line-height: .8;
  color: transparent; -webkit-text-stroke: 2px var(--z-red);
  font-variant-numeric: tabular-nums;
}
.z-cal .z-spread-eyebrow {
  font-family: var(--z-font-cond); font-size: .72rem; font-weight: 600; letter-spacing: .28em;
  text-transform: uppercase; color: var(--z-sizzle); margin: .3rem 0 .4rem;
}
.z-cal .z-spread-head h2 {
  font-family: var(--z-font-cond); text-transform: uppercase; font-weight: 700;
  font-size: clamp(1.9rem, 5vw, 2.7rem); line-height: .98; margin: 0; letter-spacing: .01em; text-wrap: balance;
}
.z-cal .z-cols { display: grid; grid-template-columns: 1.5fr 1fr; gap: 2.4rem; align-items: start; }

/* scrollable month listing — opens anchored on TODAY; past weeks live above */
.z-cal .z-scroll {
  max-height: 620px; overflow-y: auto; position: relative;
  padding-right: .7rem;
  scrollbar-width: thin; scrollbar-color: var(--z-charm) transparent;
  /* soft fades hint that more content exists in both directions */
  -webkit-mask-image: linear-gradient(180deg, transparent 0, #000 34px, #000 calc(100% - 26px), transparent 100%);
          mask-image: linear-gradient(180deg, transparent 0, #000 34px, #000 calc(100% - 26px), transparent 100%);
}
.z-cal .z-scroll::-webkit-scrollbar { width: 8px; }
.z-cal .z-scroll::-webkit-scrollbar-thumb { background: var(--z-baby); border-radius: 4px; }
.z-cal .z-scroll-sign { position: sticky; top: 2px; z-index: 3; display: flex; justify-content: center; height: 0; }
.z-cal .z-scroll-sign button {
  border: 0; font-family: var(--z-font-cond); font-size: .62rem; font-weight: 700;
  letter-spacing: .22em; text-transform: uppercase;
  background: var(--z-ink); color: var(--z-paper);
  border-radius: 999px; padding: .32rem .95rem; transform: rotate(-1deg);
  box-shadow: 0 6px 14px -6px rgba(26,16,24,.45);
}
.z-cal .z-scroll-sign button:hover { background: var(--z-red); }

/* weekly listing (left column) */
.z-cal .z-wk { margin-bottom: 1.9rem; }
.z-cal .z-wk:first-of-type { margin-top: 1.6rem; }
.z-cal .z-wk-label {
  font-family: var(--z-font-cond); font-size: .72rem; font-weight: 600; letter-spacing: .26em;
  text-transform: uppercase; color: var(--z-warm-gray); margin: 0 0 .3rem;
  display: flex; align-items: center; gap: .9rem;
}
.z-cal .z-wk-label::after { content: ''; flex: 1; height: 1px; background: var(--z-line); }
.z-cal .z-wk-label .z-now { color: var(--z-red); }
.z-cal .z-day {
  display: grid; grid-template-columns: 3.6rem 1fr; gap: 1.1rem; align-items: start;
  padding: .95rem 0; border-bottom: 1px dashed var(--z-line); cursor: pointer;
}
.z-cal .z-day:last-child { border-bottom: 0; }
.z-cal .z-day .z-dayno {
  font-family: var(--z-font-cond); font-weight: 700; font-size: 2rem; line-height: .9;
  color: var(--z-ink); font-variant-numeric: tabular-nums;
}
.z-cal .z-day .z-dayno small {
  display: block; font-size: .58rem; letter-spacing: .26em; text-transform: uppercase;
  color: var(--z-warm-gray); font-weight: 600; margin-top: .25rem;
}
.z-cal .z-entry { display: flex; align-items: baseline; gap: .8rem; padding: .1rem 0; min-width: 0; }
.z-cal .z-entry + .z-entry { margin-top: .45rem; }
/* V7 "research baseline" event-row typography: mixed-case UI-sans title at a
   full 16px, times bumped to match, kind stamp kept as caps (single-word)
   with an AA-safe label ink, plus a quiet "with <host>" sub-line. */
.z-cal .z-entry .z-time { font-family: var(--z-font-cond); font-weight: 600; font-size: .95rem; color: var(--z-mid); flex: none; width: 2.9rem; font-variant-numeric: tabular-nums; }
.z-cal .z-entry .z-et { font-family: var(--z-font-ui); text-transform: none; letter-spacing: 0; font-weight: 600; font-size: 1rem; line-height: 1.45; }
.z-cal .z-entry .z-ek {
  display: inline-block; font-family: var(--z-font-cond); font-size: .62rem; font-weight: 700;
  letter-spacing: .2em; text-transform: uppercase; margin-left: .55rem; position: relative; top: -.1em;
}
/* kind label ink is data-driven: each entry sets --k-ink inline (AA-safe) */
.z-cal .z-entry .z-ek { color: var(--k-ink); }
.z-cal .z-entry .z-entry-body { min-width: 0; }
.z-cal .z-entry .z-sub { display: block; font-size: .8rem; color: var(--z-mid); margin-top: .1rem; }
/* running right now — the sub-line turns red with a slow pulsing dot */
.z-cal .z-entry .z-sub--live { color: var(--z-red); font-weight: 600; }
.z-cal .z-entry .z-sub--live::before {
  content: ''; display: inline-block; width: 6px; height: 6px; border-radius: 999px;
  background: var(--z-red); margin-right: .4rem; vertical-align: middle;
  animation: z-pulse 1.7s ease-in-out infinite;
}
@keyframes z-pulse { 0%, 100% { opacity: 1; } 50% { opacity: .3; } }
.z-cal .z-day--past { opacity: .5; }
.z-cal .z-day--past .z-dayno { color: var(--z-warm-gray); }

/* today = a taped clip pinned into the listing */
.z-cal .z-day--today {
  background: #fff; border: 1px solid var(--z-line); border-radius: 4px;
  padding: 1rem 1.1rem; margin: .55rem 0; position: relative;
  transform: rotate(-.5deg); box-shadow: 0 6px 16px -10px rgba(26,16,24,.25);
  border-bottom: 1px solid var(--z-line);
}
.z-cal .z-day--today::before {
  content: ''; position: absolute; top: -9px; left: 50%; transform: translateX(-50%) rotate(-2deg);
  width: 58px; height: 16px; background: rgba(242,160,196,.55);
}
.z-cal .z-day--today .z-dayno { color: var(--z-red); }
.z-cal .z-day--today .z-dayno small { color: var(--z-red); }
.z-cal .z-today-flag {
  display: inline-block; font-family: var(--z-font-cond); font-size: .6rem; font-weight: 700;
  letter-spacing: .24em; text-transform: uppercase; color: #fff; background: var(--z-red);
  border-radius: 3px; padding: .18rem .55rem; transform: rotate(-1.2deg); margin-bottom: .35rem;
}
/* actions belong to a single entry, so a two-event today is never ambiguous */
.z-cal .z-entry--today + .z-entry--today { margin-top: .95rem; }
.z-cal .z-entry-actions { display: flex; flex-wrap: wrap; gap: .5rem; margin-top: .55rem; }
.z-cal .z-entry-actions .z-join-btn { padding: .45rem 1.1rem; font-size: .72rem; }
.z-cal mark { background: linear-gradient(transparent 60%, var(--z-baby) 60%); color: inherit; padding: 0 .08em; font-weight: 600; }

/* mini-month clip (right column) */
.z-cal .z-aside { position: sticky; top: 1.2rem; }
.z-cal .z-clip {
  background: #fff; border: 1px solid var(--z-line); border-radius: 4px;
  padding: 1.1rem 1.2rem 1.2rem; position: relative;
  transform: rotate(.6deg); box-shadow: 0 6px 16px -10px rgba(26,16,24,.25);
  margin-bottom: 1.6rem;
}
.z-cal .z-clip::before {
  content: ''; position: absolute; top: -9px; left: 50%; transform: translateX(-50%) rotate(-2deg);
  width: 58px; height: 16px; background: rgba(242,160,196,.55);
}
.z-cal .z-clip h3 {
  font-family: var(--z-font-cond); text-transform: uppercase; font-weight: 700;
  font-size: 1.05rem; letter-spacing: .05em; margin: 0 0 .7rem;
}
.z-cal .z-mini { width: 100%; border-collapse: collapse; text-align: center; }
.z-cal .z-mini th {
  font-family: var(--z-font-cond); font-size: .58rem; letter-spacing: .18em; text-transform: uppercase;
  color: var(--z-warm-gray); font-weight: 600; padding-bottom: .45rem;
}
.z-cal .z-mini td { padding: .12rem 0; }
.z-cal .z-d {
  width: 30px; height: 30px; margin: 0 auto; border-radius: 999px;
  display: flex; align-items: center; justify-content: center;
  font-family: var(--z-font-cond); font-weight: 600; font-size: .8rem; position: relative; cursor: pointer;
  font-variant-numeric: tabular-nums;
}
.z-cal .z-d:hover { background: var(--z-baby); }
.z-cal .z-d.out { color: #D3A9B4; }
.z-cal .z-d.dot::after {
  content: ''; position: absolute; bottom: 1px; left: 50%; transform: translateX(-50%);
  width: 4px; height: 4px; border-radius: 999px; background: var(--k-dot);
}
.z-cal .z-d.today { background: var(--z-red); color: #fff; font-weight: 700; }
.z-cal .z-d.today::after { background: #fff; }
/* multi-event day: stacked spot-ink dots (--k-dot / --k-dot2 set inline) */
.z-cal .z-d.dot.multi::after {
  transform: translateX(calc(-50% - 4px));
  box-shadow: 8px 0 0 0 var(--k-dot2);
}
/* jump-flash when the side calendar scrolls the list to a day */
@keyframes z-flash { 0%, 100% { background: transparent; } 30% { background: var(--z-baby); } }
.z-cal .z-day.z-flash { animation: z-flash 1.3s ease-out 1; border-radius: 4px; }
.z-cal .z-day--today.z-flash { animation: none; }

/* legend: dashed workbook panel */
.z-cal .z-workbook {
  border: 2px dashed var(--z-red-applied); border-radius: 6px; background: var(--z-cream);
  padding: 1.2rem 1.3rem 1.1rem; position: relative;
}
.z-cal .z-wb-tab {
  position: absolute; top: -14px; left: 18px;
  background: var(--z-red-applied); color: #fff;
  font-family: var(--z-font-cond); font-size: .7rem; font-weight: 700; letter-spacing: .24em; text-transform: uppercase;
  padding: .3rem .85rem; border-radius: 3px; transform: rotate(-1.2deg);
}
.z-cal .z-legend-row { display: flex; align-items: center; gap: .7rem; padding: .3rem 0; }
.z-cal .z-legend-row i { width: 9px; height: 9px; border-radius: 999px; flex: none; }
.z-cal .z-legend-row span { font-family: var(--z-font-cond); text-transform: uppercase; letter-spacing: .12em; font-size: .74rem; font-weight: 600; }
.z-cal .z-legend-row small { font-size: .72rem; color: var(--z-warm-gray); margin-left: auto; text-align: right; }
.z-cal .z-wb-note { font-family: var(--z-font-serif); font-style: italic; font-size: .86rem; color: var(--z-mid); margin: .7rem 0 0; }

/* resource links: second workbook panel of pill link-buttons */
.z-cal .z-resources { margin-top: 1.8rem; }
.z-cal .z-res-btn {
  display: block; text-align: center; text-decoration: none;
  font-family: var(--z-font-cond); font-size: .72rem; font-weight: 700; letter-spacing: .18em; text-transform: uppercase;
  border: 2px solid var(--z-ink); border-radius: 999px; padding: .5rem 1rem;
  color: var(--z-ink); background: none; transition: background .13s, color .13s;
}
.z-cal .z-res-btn + .z-res-btn { margin-top: .6rem; }
.z-cal .z-res-btn:hover { background: var(--z-ink); color: var(--z-paper); }

/* ═══ EVENT DETAIL — a zine clip as a dialog ═══ */
.z-cal .z-modal-wrap {
  position: fixed; inset: 0; z-index: 60; display: none;
  align-items: center; justify-content: center; padding: 1.2rem;
  background: rgba(26,16,24,.55);
}
.z-cal .z-modal-wrap.open { display: flex; }
.z-cal .z-modal {
  background: #fff; border: 1px solid var(--z-line); border-radius: 6px;
  width: 100%; max-width: 400px; padding: 1.7rem 1.7rem 1.5rem; position: relative;
  transform: rotate(-.6deg); box-shadow: 0 30px 60px -18px rgba(26,16,24,.6);
}
.z-cal .z-modal::before {
  content: ''; position: absolute; top: -10px; left: 50%; transform: translateX(-50%) rotate(-2deg);
  width: 68px; height: 18px; background: rgba(242,160,196,.6);
}
.z-cal .z-m-close {
  position: absolute; top: .55rem; right: .7rem; border: 0; background: none;
  font-size: 1.05rem; color: var(--z-warm-gray); line-height: 1; padding: .3rem;
}
.z-cal .z-m-close:hover { color: var(--z-red); }
.z-cal .z-m-kind {
  font-family: var(--z-font-cond); font-size: .64rem; font-weight: 700;
  letter-spacing: .26em; text-transform: uppercase; margin: 0 0 .4rem;
}
.z-cal .z-m-kind { color: var(--k-ink); }
.z-cal .z-modal h3 {
  font-family: var(--z-font-cond); text-transform: uppercase; font-weight: 700;
  font-size: 1.55rem; line-height: 1.05; margin: 0 0 .4rem; letter-spacing: .02em; text-wrap: balance;
}
.z-cal .z-m-when { font-family: var(--z-font-serif); font-style: italic; font-size: 1rem; color: var(--z-mid); margin: 0 0 1rem; }
.z-cal .z-m-row {
  display: flex; align-items: center; gap: .8rem; padding: .5rem 0;
  border-top: 1px dashed var(--z-line); font-size: .85rem;
}
.z-cal .z-m-row .z-m-label {
  font-family: var(--z-font-cond); font-size: .6rem; font-weight: 700; letter-spacing: .22em;
  text-transform: uppercase; color: var(--z-warm-gray); width: 3.4rem; flex: none;
}
.z-cal .z-m-avatar {
  width: 36px; height: 36px; border-radius: 50%; flex: none;
  background: radial-gradient(circle at 32% 28%, #F78DA7, var(--z-red) 75%);
  color: #fff; display: grid; place-items: center;
  font-family: var(--z-font-cond); font-weight: 700; font-size: .95rem;
  box-shadow: 0 0 0 3px rgba(242,160,196,.35);
}
.z-cal .z-m-actions { display: flex; gap: .7rem; margin-top: 1.15rem; flex-wrap: wrap; }
.z-cal .z-btn-dark {
  border: 0; border-radius: 999px; padding: .55rem 1.3rem;
  font-family: var(--z-font-cond); font-weight: 700; font-size: .74rem;
  letter-spacing: .18em; text-transform: uppercase;
  background: var(--z-ink); color: #fff; transition: transform .13s;
}
.z-cal .z-btn-dark:hover { transform: translateY(-2px); }
.z-cal .z-m-note {
  font-family: var(--z-font-serif); font-style: italic; font-size: .84rem;
  color: var(--z-warm-gray); margin: 1.1rem 0 0;
}

/* ═══ INTERLUDE ═══ */
.z-cal .z-interlude { background: var(--z-baby); }
.z-cal .z-interlude .z-page { padding-top: 2.7rem; padding-bottom: 2.7rem; text-align: center; }
.z-cal .z-interlude .z-q {
  font-family: var(--z-font-serif); font-style: italic;
  font-size: clamp(1.3rem, 3.4vw, 1.85rem); line-height: 1.35; color: var(--z-ink);
  max-width: 28ch; margin: 0 auto .8rem; text-wrap: balance;
}
.z-cal .z-interlude .z-who { font-family: var(--z-font-cond); font-size: .74rem; letter-spacing: .3em; text-transform: uppercase; color: var(--z-red); font-weight: 600; margin: 0; }

/* ═══ Mobile behavior ═══ */
@media (max-width: 640px) {
  .z-cal .z-cols { grid-template-columns: 1fr; gap: 1.9rem; }
  /* month-at-a-glance jumps above the listing on phones */
  .z-cal .z-aside { position: static; order: -1; }
  .z-cal .z-spread-head { grid-template-columns: auto 1fr; gap: 1rem; }
  .z-cal .z-monthbar { grid-column: 1 / -1; justify-self: start; }
  .z-cal .z-bigno { font-size: 3.4rem; }
  .z-cal .z-scroll { max-height: 58vh; }
  .z-cal .z-day { grid-template-columns: 3rem 1fr; gap: .8rem; }
  .z-cal .z-day .z-dayno { font-size: 1.6rem; }
  .z-cal .z-entry { flex-wrap: wrap; gap: .35rem .8rem; }
}
`;

/**
 * Renders the Programme-calendar stylesheet once. ProgrammeCalendar already
 * includes this for you.
 */
export function ProgrammeCalendarStyles() {
  return <style dangerouslySetInnerHTML={{ __html: programmeCalendarCss }} />;
}
